<template>
  <div
      v-if="visible"
      class="chamber"
      :class="{ 'chamber--awake': isAwake, 'chamber--death': isDeathSave }"
      :style="chamberStyle"
  >
    <!-- The chamber itself: photographic plate, die composited into the ring -->
    <div class="chamber__plate">
      <!-- Cards ring the drop zone and sit beneath the fog, so the haze drifts
           in front of them and they read as standing in the room. -->
      <DiceModifierHolder
          v-if="props.challenge?.modifiers?.length"
          class="chamber__modifiers"
          :modifiers="props.challenge.modifiers"
          :highlighted-index="currentlyHighlightedModifier"
          :is-animating="isAnimatingModifiers"
      />

      <!-- Procedural fog drifting over the whole room, above the plate -->
      <canvas ref="fogCanvas" class="chamber__fog" aria-hidden="true"></canvas>

      <div class="ring-anchor" @click="rollDice">
        <DiceCanvas
            ref="diceCanvas"
            :key="theme.id"
            :theme="theme.id"
            @ready="onCanvasReady"
            @error="onCanvasError"
            @roll-complete="onRollComplete"
            class="dice-canvas"
        />
      </div>

      <!-- Only the threshold is short enough to stay legible inscribed on stone -->
      <div class="floor-text">
        <p class="floor-text__threshold">
          <span class="floor-text__label">{{ t('diceRoller.modal.difficulty') }}</span>
          <span class="floor-text__value">{{ props.challenge?.difficulty || 14 }}</span>
        </p>
      </div>

      <!-- The verdict rises out of the ring -->
      <div class="verdict" :class="outcomeClass" v-if="currentState === 'results' && lastResult">
        <span class="verdict__number">{{ lastResult.number }}</span>
        <span class="verdict__rule"></span>
        <span class="verdict__label">{{ outcomeLabel }}</span>
      </div>
    </div>

    <!-- Ambient HUD, held at the edges so the chamber stays the subject -->
    <div class="hud">
      <!-- A death save is the same d20 against the same server-set difficulty;
           what changes is that the player has to know what is being decided,
           how close it already is, and that walking away is a failure. -->
      <div v-if="isDeathSave" class="hud__death">
        <span class="hud__death-title">{{ t('diceRoller.modal.deathSaveTitle') }}</span>
        <span class="hud__death-progress">
          {{ t('diceRoller.modal.deathSaveProgress', deathSaveCounts) }}
        </span>
        <span class="hud__death-warning">{{ t('diceRoller.modal.deathSaveWarning') }}</span>
      </div>

      <p class="hud__brief">{{ descriptionText }}</p>

      <button
          v-if="showCTA"
          type="button"
          class="hud__cast"
          @click="rollDice"
      >
        {{ t('diceRoller.modal.rollCta') }}
      </button>
      <span v-else-if="currentState === 'rolling'" class="hud__casting">
        {{ t('diceRoller.modal.casting') }}
      </span>

      <button
          v-if="canSkipAnimation && !isSkipRequested"
          type="button"
          class="hud__skip"
          @click="skipAnimation"
      >
        {{ t('diceRoller.modal.skipAnimation') }}
      </button>

      <button type="button" class="hud__leave" @click="closeModal">
        {{ t('diceRoller.modal.leave') }}
      </button>

    </div>

    <!-- Coming to: dark, then the chamber resolves -->
    <div class="eyelids" aria-hidden="true">
      <span class="eyelids__top"></span>
      <span class="eyelids__bottom"></span>
    </div>

    <DiceDebugToolbar
        v-if="props.debug"
        :dice-canvas="diceCanvas"
        :visible="props.debug"
        @roll-started="onDebugRollStarted"
        @roll-completed="onDebugRollCompleted"
        @visibility-changed="onDebugVisibilityChanged"
    />
  </div>
</template>
<script setup lang="ts">
import {ref, computed, watch, nextTick, onMounted, onBeforeUnmount} from 'vue';
import {useI18n} from 'vue-i18n';
import DiceCanvas from "@/components/DiceRoller/DiceCanvas.vue";
import DiceBackendService from "@/services/dice/DiceBackendService.js";
import DiceModifierHolder from "@/components/DiceRoller/DiceModifier/DiceModifierHolder.vue";
import DiceDebugToolbar from "@/components/DiceRoller/DiceDebugToolbar.vue";
import ChamberFog from "@/services/dice/ChamberFog.js";
import {getDiceTheme} from "@/services/dice/diceThemes.js";
import type {ChallengeGeneric} from "@/api/dx-backend/api";

// Define types for component
interface RollResult {
  number: number;
  rollTime: number;
  targetNumber?: number;
  outcome?: string;

  [key: string]: any; // For any additional properties in the result
}

// Define outcome types
type OutcomeType = 'Critical Fail' | 'Fail' | 'Success' | 'Critical Success';

/** A downed fighter's tally, as `FightParticipant.death_save` states it. */
type DeathSaveProgress = {
  failures: number;
  successes: number;
  pending?: boolean;
};

type Props = {
  visible: boolean;
  debug?: boolean | undefined;
  challenge?: ChallengeGeneric | null | undefined;
  /** Overrides where the roll comes from; used to drive the roller in isolation. */
  rollSource?: (() => Promise<RollResult>) | null;
  /**
   * Set only while this challenge is a death save. Its presence is what turns
   * the roller from an anonymous d20 into one; the caller decides that from
   * the fight state, since a save arrives as an ordinary challenge.
   */
  deathSave?: DeathSaveProgress | null;
  /** Server-set thresholds. Never hardcoded here — they are tunable. */
  deathSaveFailuresToComa?: number;
  deathSaveSuccessesToRecover?: number;
};

// Props
const props = withDefaults(defineProps<Props>(), {
  visible: false,
  debug: false,
  challenge: null,
  rollSource: null,
  deathSave: null,
  deathSaveFailuresToComa: 3,
  deathSaveSuccessesToRecover: 3
});

// Emits
const emit = defineEmits<{
  close: [];
  error: [error: Error];
  'roll-complete': [result: RollResult];
}>();

const {t} = useI18n();

// Reactive data
const currentState = ref<'initial' | 'rolling' | 'results' | 'applying-modifiers'>('initial');
const isCanvasReady = ref(false);
const lastResult = ref<RollResult | null>(null);
const lastApiResult = ref<RollResult | null>(null);
const currentOutcome = ref<OutcomeType | null>(null);
const showCTA = ref(true);
const diceBackendService = new DiceBackendService();

// Animation state for modifiers
const isAnimatingModifiers = ref(false);
const currentlyHighlightedModifier = ref<number>(-1);
const modifiedRollValue = ref<number>(0);
const originalRollValue = ref<number>(0);

// Every die on the table, so modifiers land on the pair rather than only on
// whichever die happened to win the raw roll.
const rolledValues = ref<number[]>([]);
const modifiedValues = ref<number[]>([]);
const keepMode = ref<'best' | 'worst'>('best');

const clampFace = (value: number): number => Math.max(1, Math.min(20, value));

/** Index of the die that counts once modifiers have been applied. */
const keptIndex = computed((): number => {
  const values = modifiedValues.value;
  if (!values.length) return 0;

  let index = 0;
  values.forEach((value, i) => {
    const better = keepMode.value === 'worst' ? value < values[index] : value > values[index];
    if (better) index = i;
  });
  return index;
});

const keptValue = computed((): number => modifiedValues.value[keptIndex.value] ?? 0);

// Skip animation state
const canSkipAnimation = ref(false);
const isSkipRequested = ref(false);

// Drives the come-to sequence: black, a blink, then the chamber resolves.
const isAwake = ref(false);

/**
 * What kind of roll this is gets settled when the roller opens, and nothing
 * that happens afterwards may change it. The fight clock keeps moving while
 * the die is in the air — the save resolves, the tally is cleared, the
 * character stands up — and reading the live prop meant the room, the die and
 * the whole canvas were swapped out mid-roll, taking the throw and its verdict
 * with them.
 */
const latchedDeathSave = ref<DeathSaveProgress | null>(props.visible ? props.deathSave ?? null : null);
const isDeathSave = computed((): boolean => latchedDeathSave.value !== null);

/**
 * The one exception: the fight state can still be catching up at the moment the
 * challenge event opens the roller. A save may therefore arrive a beat late —
 * but only before the first throw, and never as a downgrade.
 */
watch(() => props.deathSave, (save) => {
  if (!props.visible || !save) return;
  if (currentState.value !== 'initial') return;
  latchedDeathSave.value = save;
});

/**
 * A death save is not dressed like an ordinary check: the roller wears the death
 * theme whenever one is running, and the default chamber otherwise.
 */
const theme = computed(() => getDiceTheme(isDeathSave.value ? 'death' : 'default'));

/** The stylesheet reads colours only through these; the theme owns the values. */
const chamberStyle = computed((): Record<string, string> => ({
  ...theme.value.css,
  '--plate': `url(${theme.value.plate})`,
  '--ring-x': `${theme.value.ring.x * 100}%`,
  '--ring-y': `${theme.value.ring.y * 100}%`,
  '--ring-size': `${theme.value.ring.size * 100}%`,
  '--plate-ratio': String(theme.value.plateRatio),
  '--ring-lift': `${theme.value.ring.lift}%`,
  '--fog-opacity': String(theme.value.fog.opacity)
}));

const deathSaveCounts = computed(() => ({
  failures: latchedDeathSave.value?.failures ?? 0,
  failuresLimit: props.deathSaveFailuresToComa,
  successes: latchedDeathSave.value?.successes ?? 0,
  successesLimit: props.deathSaveSuccessesToRecover,
}));

const descriptionText = computed((): string => {
  if (isDeathSave.value) return t('diceRoller.modal.deathSaveBrief');
  return props.challenge?.description || t('diceRoller.modal.defaultDescription');
});

const OUTCOME_KEYS: Record<OutcomeType, string> = {
  'Critical Fail': 'diceRoller.modal.outcomes.criticalFail',
  'Fail': 'diceRoller.modal.outcomes.fail',
  'Success': 'diceRoller.modal.outcomes.success',
  'Critical Success': 'diceRoller.modal.outcomes.criticalSuccess'
};

const outcomeLabel = computed((): string => {
  if (!currentOutcome.value) return '';
  const key = OUTCOME_KEYS[currentOutcome.value];
  return key ? t(key) : currentOutcome.value;
});
// Template refs
const diceCanvas = ref<InstanceType<typeof DiceCanvas> | null>(null);
const fogCanvas = ref<HTMLCanvasElement | null>(null);

const fog = new ChamberFog();
const onFogResize = () => fog.resize();
/** The room the running fog was mixed for; null while there is none. */
let fogThemeId: string | null = null;

const startFog = async (): Promise<void> => {
  await nextTick();
  if (!fogCanvas.value) return;

  // The chamber leaves the DOM whenever the roller is hidden, so a fog held
  // over from a previous opening is bound to a canvas that no longer exists —
  // and it was mixed for whichever room that roll happened in.
  const stale = fog.renderer
      && (fog.renderer.domElement !== fogCanvas.value || fogThemeId !== theme.value.id);
  if (stale) fog.dispose();
  if (fog.renderer) return;
  fogThemeId = theme.value.id;

  const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const {ring, fog: fogTheme} = theme.value;
  fog.init(fogCanvas.value, {
    ringX: ring.x,
    ringY: ring.y,
    color: fogTheme.color,
    intensity: (prefersReduced ? 0.38 : 0.76) * fogTheme.intensity
  });
  if (!prefersReduced) fog.start();
  else fog.renderer.render(fog.scene, fog.camera);

  window.addEventListener('resize', onFogResize);
};

// Computed properties
const canRoll = computed((): boolean => {
  return isCanvasReady.value && currentState.value !== 'rolling';
});

const outcomeClass = computed((): string => {
  if (!currentOutcome.value) return '';

  switch (currentOutcome.value) {
    case 'Critical Fail':
      return 'outcome-critical-fail';
    case 'Fail':
      return 'outcome-fail';
    case 'Success':
      return 'outcome-success';
    case 'Critical Success':
      return 'outcome-critical-success';
    default:
      return '';
  }
});

// Methods
const closeModal = (): void => {
  emit('close');
};

const onCanvasReady = async (): Promise<void> => {
  isCanvasReady.value = true;
};

const onCanvasError = (error: Error): void => {
  console.error('Canvas error:', error);
  emit('error', error);
};


const rollDice = async (): Promise<void> => {
  if (!canRoll.value || !diceCanvas.value) return;

  try {
    currentState.value = 'rolling';
    showCTA.value = false; // Hide CTA after first roll

    // Enable skip animation during rolling
    canSkipAnimation.value = true;
    isSkipRequested.value = false;

    // Get dice roll result from backend API
    const apiResult = props.rollSource
      ? await props.rollSource()
      : await diceBackendService.rollD20Dice();

    // Store the API result BEFORE starting visual animation
    lastApiResult.value = apiResult;

    // The backend decides the number; the seed makes the throw that reveals it
    // reproducible, so the same challenge always plays out identically.
    const rolls = apiResult.rolls?.length ? apiResult.rolls : [apiResult.number];
    const seed = `${props.challenge?.id ?? 'adhoc'}:${rolls.join('-')}`;
    const pair = (apiResult.advantage || apiResult.disadvantage) && rolls.length > 1;

    keepMode.value = apiResult.disadvantage ? 'worst' : 'best';
    rolledValues.value = pair ? [...rolls] : [apiResult.number];
    modifiedValues.value = [...rolledValues.value];

    // Advantage and disadvantage put a second die in the ring; the kept die is
    // the better or the worse of the pair.
    diceCanvas.value.setDiceCount(pair ? 2 : 1);
    diceCanvas.value.setResultMode(apiResult.disadvantage ? 'worst' : 'best');

    const result = pair
      ? await diceCanvas.value.rollToTargets(rolls, { seed })
      : await diceCanvas.value.rollToTarget(apiResult.number, { seed });

    lastResult.value = result;

    // Result will be handled by onRollComplete
  } catch (error) {
    console.error('Error rolling dice:', error);
    currentState.value = 'initial';
  }
};

const onRollComplete = (result: RollResult): void => {
  // Ensure we have fresh API result data - if not, something went wrong
  if (!lastApiResult.value) {
    console.warn('onRollComplete called without API result data');
    return;
  }

  // Combine the visual roll result with the API result, prioritizing API data
  const combinedResult = {
    ...result,
    // Always use the API result for the actual outcome
    number: lastApiResult.value.number,
    targetNumber: lastApiResult.value.targetNumber
  };

  lastResult.value = combinedResult;
  originalRollValue.value = combinedResult.number;
  modifiedRollValue.value = combinedResult.number;

  // Always use the outcome from the API result, which is determined by the service
  if (lastApiResult.value?.outcome) {
    currentOutcome.value = lastApiResult.value.outcome as OutcomeType;
  } else {
    // If for some reason we don't have an API result, use the service to determine the outcome
    currentOutcome.value = diceBackendService.determineOutcome(combinedResult.number) as OutcomeType;
  }

  currentState.value = 'results';

  // Check if we have modifiers to animate and if the roll is not critical
  if (props.challenge?.modifiers && props.challenge.modifiers.length > 0 && !isCriticalRoll(combinedResult.number)) {
    // Check if skip was requested during rolling
    if (isSkipRequested.value) {
      // Skip directly to final result with all modifiers applied
      skipModifierAnimation();
    } else {
      // Start modifier animation sequence for non-critical rolls
      setTimeout(() => {
        animateModifiers();
      }, 1000); // Wait 1 second after initial roll result display
    }
  } else {
    // No modifiers, critical roll, or no modifiers to apply - emit result immediately
    // For critical rolls, preserve the original critical outcome regardless of modifiers
    canSkipAnimation.value = false;
    emit('roll-complete', combinedResult);
  }

  // Clear the API result after using it
  lastApiResult.value = null;
};

// Debug toolbar event handlers
const onDebugRollStarted = (debugInfo: any): void => {
  console.log('Debug roll started:', debugInfo);
  currentState.value = 'rolling';
};

const onDebugRollCompleted = (result: RollResult): void => {
  console.log('Debug roll completed:', result);
  lastResult.value = result;

  // For debug rolls, determine outcome based on the result number
  currentOutcome.value = diceBackendService.determineOutcome(result.number) as OutcomeType;
  currentState.value = 'results';

  // Emit the debug result
  emit('roll-complete', result);
};

const onDebugVisibilityChanged = (visible: boolean): void => {
  console.log('Debug toolbar visibility changed:', visible);
};

// Helper function to check if a roll is critical (1 or 20)
const isCriticalRoll = (rollValue: number): boolean => {
  return rollValue === 1 || rollValue === 20;
};

// Skip animation functionality
const skipAnimation = (): void => {
  if (!canSkipAnimation.value || isSkipRequested.value) return;

  isSkipRequested.value = true;
  canSkipAnimation.value = false;

  // Handle different animation states
  if (currentState.value === 'rolling') {
    // Skip dice rolling animation - wait for roll to complete naturally but skip modifier animation
    // The skip will be handled in onRollComplete
    return;
  }

  if (currentState.value === 'applying-modifiers') {
    // Skip modifier animation and calculate final result immediately
    skipModifierAnimation();
  }
};

const skipModifierAnimation = (): void => {
  if (!props.challenge?.modifiers || !lastResult.value) return;

  // Stop current animation
  isAnimatingModifiers.value = false;
  currentlyHighlightedModifier.value = -1;

  // Apply every remaining card to every die at once
  const total = props.challenge.modifiers.reduce((sum, modifier) => sum + modifier.value, 0);
  modifiedValues.value = rolledValues.value.map(value => value + total);
  modifiedRollValue.value = keptValue.value;

  if (diceCanvas.value) {
    modifiedValues.value.forEach((value, index) => {
      diceCanvas.value?.showFace(clampFace(value), index, 0.3).catch(error => {
        console.warn('Could not update dice to show final number:', error);
      });
    });
    diceCanvas.value.setKeptDie(keptIndex.value, clampFace(keptValue.value));
  }

  // Create final result
  const finalResult = {
    ...lastResult.value,
    number: keptValue.value,
    numbers: [...modifiedValues.value],
    originalNumber: originalRollValue.value,
    modifiedBy: keptValue.value - originalRollValue.value
  };

  // Determine final outcome
  currentOutcome.value = diceBackendService.determineOutcome(keptValue.value) as OutcomeType;

  lastResult.value = finalResult;
  currentState.value = 'results';
  emit('roll-complete', finalResult);
};

// Modifier animation functions
const animateModifiers = async (): Promise<void> => {
  if (!props.challenge?.modifiers || props.challenge.modifiers.length === 0) {
    return;
  }

  currentState.value = 'applying-modifiers';
  isAnimatingModifiers.value = true;

  // Enable skip animation during modifier animation
  canSkipAnimation.value = true;

  // Animate each modifier sequentially
  for (let i = 0; i < props.challenge.modifiers.length; i++) {
    // Check if skip was requested
    if (isSkipRequested.value) {
      skipModifierAnimation();
      return;
    }

    const modifier = props.challenge.modifiers[i];

    // Highlight current modifier
    currentlyHighlightedModifier.value = i;

    // Wait for highlight animation (with skip check)
    await new Promise(resolve => setTimeout(resolve, 800));

    // Check again after delay
    if (isSkipRequested.value) {
      skipModifierAnimation();
      return;
    }

    // A card affects every die on the table; the better or worse of them is
    // only chosen once all the cards have landed.
    modifiedValues.value = modifiedValues.value.map(value => value + modifier.value);
    modifiedRollValue.value = keptValue.value;

    await Promise.all(
        modifiedValues.value.map((value, index) =>
            diceCanvas.value?.showFace(clampFace(value), index)
        )
    );

    diceCanvas.value?.setKeptDie(keptIndex.value, clampFace(keptValue.value));

    // The counter tracks the dice as each card lands, so they read the same value.
    lastResult.value = {...lastResult.value!, number: keptValue.value};
    currentOutcome.value = diceBackendService.determineOutcome(keptValue.value) as OutcomeType;

    // Wait before next modifier (with skip check)
    await new Promise(resolve => setTimeout(resolve, 500));
  }

  // Clear highlighting
  currentlyHighlightedModifier.value = -1;
  isAnimatingModifiers.value = false;

  // Disable skip animation as animation is complete
  canSkipAnimation.value = false;

  // Update final outcome based on the die kept after every card was applied
  const finalResult = {
    ...lastResult.value!,
    number: keptValue.value,
    numbers: [...modifiedValues.value],
    originalNumber: originalRollValue.value,
    modifiedBy: keptValue.value - originalRollValue.value
  };

  // Determine new outcome based on modified value
  currentOutcome.value = diceBackendService.determineOutcome(keptValue.value) as OutcomeType;

  lastResult.value = finalResult;
  currentState.value = 'results';
  emit('roll-complete', finalResult);
};

const shakeDiceAndUpdateNumber = async (newNumber: number): Promise<void> => {
  if (!diceCanvas.value) return;

  // The die is already cast; a modifier only changes the number it reads, so it
  // turns to the new face instead of being thrown again.
  try {
    await diceCanvas.value.showFace(newNumber, 0);
  } catch (error) {
    console.warn('Could not update dice to show new number:', error);
  }
};

// Reset state when modal opens
const resetModalState = (): void => {
  latchedDeathSave.value = props.deathSave ?? null;
  isAwake.value = false;
  nextTick(() => { isAwake.value = true; });
  startFog();
  currentState.value = 'initial';
  lastResult.value = null;
  lastApiResult.value = null;
  currentOutcome.value = null;
  showCTA.value = true;
  isAnimatingModifiers.value = false;
  currentlyHighlightedModifier.value = -1;
  modifiedRollValue.value = 0;
  originalRollValue.value = 0;
  rolledValues.value = [];
  modifiedValues.value = [];
  keepMode.value = 'best';
  canSkipAnimation.value = false;
  isSkipRequested.value = false;
};

// Watch for modal visibility changes
watch(() => props.visible, (newVisible) => {
  if (newVisible) {
    resetModalState();
  }
});

/*
 * A second challenge can arrive while the roller is still open on the last
 * verdict — the next round's death save does exactly that. The chamber is
 * reset for it in place, so nothing has to close and re-open to start a roll,
 * and the room is chosen again for the challenge that is actually pending.
 */
watch(() => props.challenge?.id, (id, previous) => {
  if (!props.visible || id === previous) return;
  resetModalState();
});

onMounted(() => {
  if (props.visible) {
    resetModalState();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onFogResize);
  fog.dispose();
  fogThemeId = null;
});

</script>

<style scoped>
/* A full-screen ritual chamber. The room is a photographic plate; the die is the
   only thing rendered in 3D, lit and shadowed to sit inside the summoning ring. */
.chamber {
  /* Fallbacks only — the live values are bound from the active dice theme. */
  --flow: #7fe4ff;
  --flow-dim: #5d94ad;
  --flow-rgb: 127, 228, 255;
  --ash: #93a8b3;
  --crit: #ffd27f;
  --bane: #ff5a4a;
  --bane-rgb: 255, 90, 74;
  --inscription: #cdeffc;
  --cast-face: #dff6ff;
  --plate: url("@/assets/images/dice/ritual-chamber.webp");
  --fog-opacity: 0.85;

  --label: 600 0.68rem/1 system-ui, -apple-system, 'Segoe UI', sans-serif;
  --track: 0.36em;

  /* Where the ring sits inside the plate, as a fraction of the plate. */
  --ring-x: 51%;
  --ring-y: 59%;
  --ring-size: 34%;
  --ring-lift: 58%;
  --plate-ratio: 1.5024;

  position: fixed;
  inset: 0;
  overflow: hidden;
  background: #000;
  z-index: 10000; /* Dice must be the primary HUD - above all other elements */
}

/* Emulates background-size: cover while staying a positioned box, so anything
   anchored in % tracks the ring no matter the viewport shape. */
.chamber__plate {
  position: absolute;
  top: 50%;
  left: 50%;
  width: max(100vw, calc(100vh * var(--plate-ratio)));
  height: max(100vh, calc(100vw / var(--plate-ratio)));
  transform: translate(-50%, -50%);
  background: var(--plate) no-repeat center center;
  background-size: 100% 100%;
}

/* Sits on the plate, beneath the die, so fog reads as being in the room. */
.chamber__fog {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  mix-blend-mode: screen;
  opacity: var(--fog-opacity);
}

.ring-anchor {
  position: absolute;
  left: var(--ring-x);
  top: var(--ring-y);
  width: var(--ring-size);
  aspect-ratio: 1 / 1;
  transform: translate(-50%, calc(-1 * var(--ring-lift)));
  cursor: pointer;
}

.dice-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

/* ── Floor inscriptions ──────────────────────────────────────────────────── */
/* Laid into the plane of the stone rather than floating over it. */
.floor-text {
  position: absolute;
  /* Under the die, not over it: the inscription is cut into the floor, so a
   * die resting on that spot covers it. */
  z-index: 2;
  left: var(--ring-x);
  top: calc(var(--ring-y) + 4%);
  width: 30%;
  transform: translateX(-50%);
  perspective: 460px;
  text-align: center;
  pointer-events: none;
}

/* Shallow enough to still read as lying on the floor, but not so raked that the
   letterforms collapse. */
.floor-text__threshold {
  margin: 0;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.8em;
  transform: rotateX(42deg);
  transform-origin: 50% 0;
  color: var(--flow);
  text-shadow:
    0 0 10px rgba(var(--flow-rgb), 0.85),
    0 0 30px rgba(var(--flow-rgb), 0.4),
    0 2px 10px rgba(0, 0, 0, 0.95);
}

.floor-text__label {
  font: var(--label);
  font-size: clamp(0.62rem, 0.8vw, 0.78rem);
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--inscription);
}

.floor-text__value {
  font-size: clamp(1.7rem, 2.6vw, 2.6rem);
  font-weight: 300;
  font-variant-numeric: tabular-nums;
  color: #eafaff;
}

/* ── Verdict ─────────────────────────────────────────────────────────────── */
.verdict {
  position: absolute;
  left: var(--ring-x);
  top: calc(var(--ring-y) - 26%);
  transform: translate(-50%, -50%);
  /* The result reads in front of the cards, never behind them. */
  z-index: 5;
  display: grid;
  justify-items: center;
  gap: 0.5rem;
  color: var(--flow);
  pointer-events: none;
}

.verdict.outcome-critical-fail { color: var(--bane); }
.verdict.outcome-fail { color: var(--ash); }
.verdict.outcome-success { color: var(--flow); }
.verdict.outcome-critical-success { color: var(--crit); }

.verdict__number {
  font-size: clamp(3rem, 6vw, 5.6rem);
  font-weight: 150;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  text-shadow: 0 0 40px currentColor;
  animation: rise 460ms cubic-bezier(0.2, 0.7, 0.3, 1) both;
}

.verdict__rule {
  width: clamp(8rem, 16vw, 16rem);
  height: 1px;
  background: linear-gradient(90deg, transparent, currentColor, transparent);
  animation: ignite 560ms 120ms cubic-bezier(0.2, 0.7, 0.3, 1) both;
}

.verdict__label {
  font: var(--label);
  letter-spacing: var(--track);
  text-transform: uppercase;
  text-shadow: 0 0 22px currentColor;
  animation: rise 460ms 200ms cubic-bezier(0.2, 0.7, 0.3, 1) both;
}

/* ── HUD ─────────────────────────────────────────────────────────────────── */
.hud {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.hud > * {
  pointer-events: auto;
}

/* Sits flat in the dark air above the ring, where nothing competes with it. */
.hud__brief {
  position: absolute;
  left: 50%;
  top: 7%;
  transform: translateX(-50%);
  margin: 0;
  width: min(46rem, 82vw);
  text-align: center;
  font-size: clamp(0.92rem, 1.25vw, 1.15rem);
  font-weight: 300;
  line-height: 1.65;
  letter-spacing: 0.06em;
  color: #dff3fb;
  text-shadow: 0 0 26px rgba(var(--flow-rgb), 0.35), 0 2px 14px rgba(0, 0, 0, 0.95);
  text-wrap: balance;
  pointer-events: none;
}

/* Above the brief, where the eye lands first: what this roll decides. */
.hud__death {
  position: absolute;
  left: 50%;
  top: 2%;
  transform: translateX(-50%);
  display: grid;
  justify-items: center;
  gap: 0.35rem;
  width: min(46rem, 82vw);
  text-align: center;
  pointer-events: none;
}

.hud__death-title {
  font: var(--label);
  font-size: clamp(0.7rem, 1vw, 0.86rem);
  letter-spacing: var(--track);
  text-transform: uppercase;
  color: var(--bane);
  text-shadow: 0 0 22px rgba(var(--bane-rgb), 0.55), 0 2px 12px rgba(0, 0, 0, 0.95);
}

.hud__death-progress {
  font-size: clamp(0.72rem, 0.95vw, 0.88rem);
  letter-spacing: 0.14em;
  font-variant-numeric: tabular-nums;
  color: #ffd9d4;
}

.hud__death-warning {
  font-size: clamp(0.62rem, 0.8vw, 0.74rem);
  letter-spacing: 0.08em;
  color: var(--ash);
}

/* The death brief is two lines taller, so it starts higher. */
.chamber--death .hud__brief {
  top: 13%;
}

.hud__cast,
.hud__casting {
  position: absolute;
  left: 50%;
  bottom: 5%;
  transform: translateX(-50%);
  font: var(--label);
  letter-spacing: var(--track);
  text-transform: uppercase;
}

.hud__cast {
  padding: 0.9rem 2.2rem;
  font-size: 0.76rem;
  background: rgba(2, 9, 14, 0.62);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(var(--flow-rgb), 0.45);
  color: var(--cast-face);
  text-shadow: 0 0 16px rgba(var(--flow-rgb), 0.6);
  cursor: pointer;
  transition: border-color 160ms ease, box-shadow 160ms ease, color 160ms ease;
  animation: breathe 3.4s ease-in-out infinite;
}

.hud__cast:hover,
.hud__cast:focus-visible {
  border-color: var(--flow);
  color: #e9fbff;
  box-shadow: 0 0 26px rgba(var(--flow-rgb), 0.25), inset 0 0 20px rgba(var(--flow-rgb), 0.08);
  outline: none;
}

.hud__casting {
  color: var(--flow-dim);
  animation: breathe 1.6s ease-in-out infinite;
}

.hud__skip,
.hud__leave {
  position: absolute;
  top: 2rem;
  padding: 0.45rem 0.9rem;
  background: transparent;
  border: 1px solid rgba(147, 168, 179, 0.22);
  color: var(--ash);
  font: var(--label);
  font-size: 0.6rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 160ms ease, border-color 160ms ease;
}

.hud__leave { right: 2rem; }
.hud__skip { right: 9.5rem; }

.hud__skip:hover,
.hud__skip:focus-visible,
.hud__leave:hover,
.hud__leave:focus-visible {
  color: var(--flow);
  border-color: rgba(var(--flow-rgb), 0.5);
  outline: none;
}

/* Anchored on the ring so the cards orbit the drop zone, under the fog. */
.chamber__modifiers {
  position: absolute;
  left: var(--ring-x);
  top: var(--ring-y);
  width: 76%;
  height: 66%;
  transform: translate(-50%, -50%);
  z-index: 1;
}

.chamber__fog {
  z-index: 2;
}

.ring-anchor {
  z-index: 3;
}

/* ── Coming to ───────────────────────────────────────────────────────────── */
/* Black, then a blink, then the chamber resolves into focus. */
/* One clip: the lids open, and the chamber is simply there. */
.eyelids__top,
.eyelids__bottom {
  position: absolute;
  left: 0;
  right: 0;
  height: 52%;
  background: #000;
  z-index: 5;
}

.eyelids__top { top: 0; transform-origin: top; }
.eyelids__bottom { bottom: 0; transform-origin: bottom; }

.chamber--awake .eyelids__top {
  animation: lidTop 900ms cubic-bezier(0.4, 0.85, 0.3, 1) forwards;
}

.chamber--awake .eyelids__bottom {
  animation: lidBottom 900ms cubic-bezier(0.4, 0.85, 0.3, 1) forwards;
}

@keyframes lidTop {
  0% { transform: scaleY(1); }
  100% { transform: scaleY(0); }
}

@keyframes lidBottom {
  0% { transform: scaleY(1); }
  100% { transform: scaleY(0); }
}

@keyframes ignite {
  from { transform: scaleX(0); opacity: 0; }
  to { transform: scaleX(1); opacity: 1; }
}

@keyframes rise {
  from { opacity: 0; transform: translateY(0.6rem); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes breathe {
  0%, 100% { opacity: 0.82; }
  50% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .eyelids__top,
  .eyelids__bottom,
  .verdict__number,
  .verdict__rule,
  .verdict__label,
  .hud__cast,
  .hud__casting {
    animation: none;
  }

  .eyelids__top,
  .eyelids__bottom {
    display: none;
  }
}

@media (max-width: 720px) {
  .ring-anchor { width: 52%; }
  .floor-text { width: 74%; }
  .hud__skip { right: 2rem; top: 5rem; }
}
</style>
